import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Phone, Mail } from "lucide-react";

const DatenschutzPage = () => {
  return (
    <>
      <SEO 
        title="Datenschutzerklärung"
        description="Datenschutzerklärung der BIS GmbH - Kfz Gutachter Schmeißer. Informationen zum Umgang mit Ihren personenbezogenen Daten gemäß DSGVO."
        canonical="/datenschutz"
      />
      {/* Hero */}
      <section className="bg-[#F7F7F7] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-black text-black mb-4 font-['Outfit']">Datenschutzerklärung</h1>
          <p className="text-[#4A4A4A]">Informationen zum Umgang mit Ihren personenbezogenen Daten</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* 1. Datenschutz auf einen Blick */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-black mb-4 font-['Outfit']">1. Datenschutz auf einen Blick</h2>
              
              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Allgemeine Hinweise</h3>
              <p className="text-[#4A4A4A] mb-4">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
                passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
                persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen 
                Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Datenerfassung auf dieser Website</h3>
              <p className="text-[#4A4A4A] mb-2"><strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong></p>
              <p className="text-[#4A4A4A] mb-4">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                können Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
              </p>

              <p className="text-[#4A4A4A] mb-2"><strong>Wie erfassen wir Ihre Daten?</strong></p>
              <p className="text-[#4A4A4A] mb-4">
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. 
                um Daten handeln, die Sie in ein Kontaktformular eingeben. Andere Daten werden automatisch oder nach 
                Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische 
                Daten (z. B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten 
                erfolgt automatisch, sobald Sie diese Website betreten.
              </p>

              <p className="text-[#4A4A4A] mb-2"><strong>Wofür nutzen wir Ihre Daten?</strong></p>
              <p className="text-[#4A4A4A] mb-4">
                Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. 
                Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
              </p>

              <p className="text-[#4A4A4A] mb-2"><strong>Welche Rechte haben Sie bezüglich Ihrer Daten?</strong></p>
              <p className="text-[#4A4A4A]">
                Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer 
                gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung 
                oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt 
                haben, können Sie diese Einwilligung jederzeit für die Zukunft widerrufen. Außerdem haben Sie das 
                Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten 
                zu verlangen. Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
              </p>
            </div>

            {/* 2. Hosting */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-black mb-4 font-['Outfit']">2. Hosting</h2>
              <p className="text-[#4A4A4A] mb-4">
                Wir hosten die Inhalte unserer Website bei folgendem Anbieter:
              </p>
              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Externes Hosting</h3>
              <p className="text-[#4A4A4A]">
                Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, 
                werden auf den Servern des Hosters gespeichert. Hierbei kann es sich v. a. um IP-Adressen, Kontaktanfragen, 
                Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe und sonstige Daten, 
                die über eine Website generiert werden, handeln. Das externe Hosting erfolgt zum Zwecke der Vertragserfüllung 
                gegenüber unseren potenziellen und bestehenden Kunden (Art. 6 Abs. 1 lit. b DSGVO) und im Interesse einer 
                sicheren, schnellen und effizienten Bereitstellung unseres Online-Angebots durch einen professionellen 
                Anbieter (Art. 6 Abs. 1 lit. f DSGVO).
              </p>
            </div>

            {/* 3. Allgemeine Hinweise und Pflichtinformationen */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-black mb-4 font-['Outfit']">3. Allgemeine Hinweise und Pflichtinformationen</h2>
              
              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Datenschutz</h3>
              <p className="text-[#4A4A4A] mb-4">
                Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre 
                personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie 
                dieser Datenschutzerklärung. Wenn Sie diese Website benutzen, werden verschiedene personenbezogene 
                Daten erhoben. Personenbezogene Daten sind Daten, mit denen Sie persönlich identifiziert werden können. 
                Die vorliegende Datenschutzerklärung erläutert, welche Daten wir erheben und wofür wir sie nutzen. Sie 
                erläutert auch, wie und zu welchem Zweck das geschieht.
              </p>
              <p className="text-[#4A4A4A] mb-4">
                Wir weisen darauf hin, dass die Datenübertragung im Internet (z. B. bei der Kommunikation per E-Mail) 
                Sicherheitslücken aufweisen kann. Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist 
                nicht möglich.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Hinweis zur verantwortlichen Stelle</h3>
              <div className="bg-[#F7F7F7] p-6 rounded-sm mb-4">
                <p className="text-[#4A4A4A]">
                  <strong className="text-black">BIS GmbH</strong><br />
                  Heidweg 1d<br />
                  25436 Uetersen<br />
                  Deutschland<br /><br />
                  Telefon: +49 174 893 6514<br />
                  E-Mail: info@bis-kfz-gutachten.de
                </p>
              </div>
              <p className="text-[#4A4A4A]">
                Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen 
                über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten (z. B. Namen, E-Mail-Adressen o. Ä.) 
                entscheidet.
              </p>
            </div>

            {/* 4. Speicherdauer */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-black mb-4 font-['Outfit']">4. Speicherdauer</h2>
              <p className="text-[#4A4A4A]">
                Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde, verbleiben 
                Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt. Wenn Sie ein 
                berechtigtes Löschersuchen geltend machen oder eine Einwilligung zur Datenverarbeitung widerrufen, 
                werden Ihre Daten gelöscht, sofern wir keine anderen rechtlich zulässigen Gründe für die Speicherung 
                Ihrer personenbezogenen Daten haben (z. B. steuer- oder handelsrechtliche Aufbewahrungsfristen); im 
                letztgenannten Fall erfolgt die Löschung nach Fortfall dieser Gründe.
              </p>
            </div>

            {/* 5. Rechte der betroffenen Person */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-black mb-4 font-['Outfit']">5. Ihre Rechte als betroffene Person</h2>
              
              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Recht auf Auskunft</h3>
              <p className="text-[#4A4A4A] mb-4">
                Sie haben das Recht, jederzeit unentgeltlich Auskunft über die zu Ihrer Person gespeicherten 
                personenbezogenen Daten sowie deren Herkunft, Empfänger und den Zweck der Datenverarbeitung zu erhalten.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Recht auf Berichtigung</h3>
              <p className="text-[#4A4A4A] mb-4">
                Sie haben das Recht, die Berichtigung unrichtiger oder die Vervollständigung Ihrer bei uns gespeicherten 
                personenbezogenen Daten zu verlangen.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Recht auf Löschung</h3>
              <p className="text-[#4A4A4A] mb-4">
                Sie haben das Recht, die Löschung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen, 
                soweit nicht die Verarbeitung zur Ausübung des Rechts auf freie Meinungsäußerung und Information, 
                zur Erfüllung einer rechtlichen Verpflichtung, aus Gründen des öffentlichen Interesses oder zur 
                Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Recht auf Einschränkung der Verarbeitung</h3>
              <p className="text-[#4A4A4A] mb-4">
                Sie haben das Recht, die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Recht auf Datenübertragbarkeit</h3>
              <p className="text-[#4A4A4A] mb-4">
                Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung oder in Erfüllung eines Vertrags 
                automatisiert verarbeiten, an sich oder an einen Dritten in einem gängigen, maschinenlesbaren Format 
                aushändigen zu lassen.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Widerspruchsrecht</h3>
              <p className="text-[#4A4A4A] mb-4">
                Soweit wir die Verarbeitung Ihrer personenbezogenen Daten auf die Interessenabwägung stützen, haben 
                Sie das Recht, Widerspruch gegen die Verarbeitung einzulegen. Bei Ausübung eines solchen Widerspruchs 
                bitten wir um Darlegung der Gründe, weshalb wir Ihre personenbezogenen Daten nicht wie von uns 
                durchgeführt verarbeiten sollten. Im Falle Ihres begründeten Widerspruchs prüfen wir die Sachlage 
                und werden entweder die Datenverarbeitung einstellen bzw. anpassen oder Ihnen unsere zwingenden 
                schutzwürdigen Gründe aufzeigen, aufgrund derer wir die Verarbeitung fortführen.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Beschwerderecht bei der Aufsichtsbehörde</h3>
              <p className="text-[#4A4A4A]">
                Unbeschadet eines anderweitigen verwaltungsrechtlichen oder gerichtlichen Rechtsbehelfs steht Ihnen 
                das Recht auf Beschwerde bei einer Aufsichtsbehörde, insbesondere in dem Mitgliedstaat Ihres Aufenthaltsorts, 
                Ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes, zu, wenn Sie der Ansicht sind, dass die 
                Verarbeitung der Sie betreffenden personenbezogenen Daten gegen die DSGVO verstößt.
              </p>
            </div>

            {/* 6. Datenerfassung auf dieser Website */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-black mb-4 font-['Outfit']">6. Datenerfassung auf dieser Website</h2>
              
              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Kontaktformular</h3>
              <p className="text-[#4A4A4A] mb-4">
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular 
                inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall 
                von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter. 
                Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage 
                mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen 
                erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse 
                an der effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf 
                Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) sofern diese abgefragt wurde.
              </p>
              <p className="text-[#4A4A4A] mb-4">
                Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung 
                auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung 
                entfällt (z. B. nach abgeschlossener Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen – 
                insbesondere Aufbewahrungsfristen – bleiben unberührt.
              </p>

              <h3 className="text-xl font-semibold text-black mb-3 font-['Outfit']">Anfrage per E-Mail, Telefon oder WhatsApp</h3>
              <p className="text-[#4A4A4A]">
                Wenn Sie uns per E-Mail, Telefon oder WhatsApp kontaktieren, wird Ihre Anfrage inklusive aller daraus 
                hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei 
                uns gespeichert und verarbeitet. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter. Die Verarbeitung 
                dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung 
                eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher Maßnahmen erforderlich ist. In allen 
                übrigen Fällen beruht die Verarbeitung auf unserem berechtigten Interesse an der effektiven Bearbeitung 
                der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer Einwilligung (Art. 6 Abs. 1 
                lit. a DSGVO) sofern diese abgefragt wurde.
              </p>
            </div>

            {/* Source */}
            <div className="mb-12 bg-[#F7F7F7] p-4 rounded-sm">
              <p className="text-sm text-[#6B7280]">
                Quelle: eRecht24
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#4A4A4A] mb-4">Fragen zum Datenschutz?</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="mailto:info@bis-kfz-gutachten.de"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#AA6622] text-white font-semibold rounded-sm hover:bg-[#8B5319] transition-colors"
            >
              <Mail className="w-4 h-4 mr-2" />
              E-Mail schreiben
            </a>
            <a 
              href="tel:+491748936514"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#AA6622] text-[#AA6622] font-semibold rounded-sm hover:bg-[#AA6622] hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" />
              Anrufen
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default DatenschutzPage;
